import type { Reducer } from 'redux';

import type { LoadEcsTreatmentsFulfilledAction } from '../../domains/ecs/actions.types';
import type {
  FeedLoadNewerThreadsAction,
  FeedLoadNewerThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { RealtimeClientLoadUriAction } from '../../domains/realtimeClient/actions.types';
import type { RealtimeDebugLogEventAction } from '../../domains/realtimeEventLog/actions.types';

import type { RealtimeEventLog, RealtimeEventLogItem, RealtimeLogEventProperties, RealtimeLogEventType } from './types';

export type RealtimeEventLogAction =
  | LoadEcsTreatmentsFulfilledAction
  | RealtimeDebugLogEventAction
  | RealtimeClientLoadUriAction
  | FeedLoadNewerThreadsAction;

const getLogEvent: (
  type: RealtimeLogEventType,
  eventName: string,
  eventProperties: RealtimeLogEventProperties
) => RealtimeEventLogItem = (type, eventName, eventProperties) => ({
  type,
  timestamp: Date.now(),
  eventName,
  eventProperties,
});

const addLogEvent: (
  state: RealtimeEventLog,
  type: RealtimeLogEventType,
  eventName: string,
  eventProperties: RealtimeLogEventProperties
) => RealtimeEventLog = (state, type, eventName, eventProperties) => ({
  ...state,
  events: [getLogEvent(type, eventName, eventProperties), ...state.events],
});

const getFeedThreadIds = (action: FeedLoadNewerThreadsFulfilledAction) => {
  const feed = action.payload.entities.feeds[action.payload.id];

  return feed ? feed.threadEdges.map((edge) => edge.nodeId) : [];
};

export const initialState: RealtimeEventLog = {
  isLoggingEnabled: false,
  events: [],
};

const logState: Reducer<RealtimeEventLog, RealtimeEventLogAction> = (state = initialState, action) => {
  if (!action || !action.type) {
    return state;
  }

  if (action.type === 'FEATURE_FLIGHTING.LOAD_ECS_TREATMENTS_FULFILLED') {
    return {
      ...state,
      isLoggingEnabled: !!action.payload.ecsFeatureRolloutTreatments.RealtimeDebugLogging,
    };
  }

  if (!state.isLoggingEnabled) {
    return state;
  }

  switch (action.type) {
    case 'FEED.LOAD_NEWER_THREADS_PENDING':
      return addLogEvent(state, 'Info', 'Newer threads requested for feed', {
        feedId: action.payload.id,
      });
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return addLogEvent(state, 'Info', 'Newer threads loaded for feed', {
        feedId: action.payload.id,
        threadIds: getFeedThreadIds(action),
      });
    case 'FEED.LOAD_NEWER_THREADS_REJECTED':
      return addLogEvent(state, 'Error', 'Newer threads request failed', action.payload);
    case 'REALTIME_CLIENT.LOAD_URI_PENDING':
      return addLogEvent(state, 'Info', 'Realtime URI requested', {});
    case 'REALTIME_CLIENT.LOAD_URI_FULFILLED':
      return addLogEvent(state, 'Info', 'Realtime URI received', { url: action.payload.uri });
    case 'REALTIME_CLIENT.LOAD_URI_REJECTED':
      return addLogEvent(state, 'Error', 'Realtime URI request failed', action.payload);
    case 'REALTIME_CLIENT.DEBUG_EVENT_LOGGED':
      return addLogEvent(state, action.payload.type, action.payload.eventName, action.payload.eventProperties);
    default:
      return state;
  }
};

export default logState;
